/**
 * Represents an event with various properties such as name
 */
export class SoliticsEvent {
    /**
     * Constructor for initializing a SoliticsEvent instance with event properties.
     * The constructor accepts a name, an optional amount, and optional custom fields.
     * Custom fields can be provided either as a JSON string or a plain JavaScript object.
     * If custom fields are not provided, a default value is used.
     *
     * @param {Object} params - Object containing event properties.
     * @param {string} params.name - The name or type of the event (transaction name).
     * @param {number} [params.amount] - The amount associated with the event (optional).
     * @param {string | object} [params.fields] - Custom fields associated with the event.
     *     Can be either a JSON string or a plain JavaScript object (optional, defaults to a predefined value).
     */
    constructor({ name, amount, fields = SoliticsEvent.DEFAULT_CUSTOM_FIELDS, }) {
        this._customFields = SoliticsEvent.DEFAULT_CUSTOM_FIELDS;
        this._name = name;
        this._amount = amount;
        // Convert object custom fields to JSON string
        if (typeof fields === 'object' && fields !== null) {
            this._customFields = JSON.stringify(fields);
        }
        else {
            this._customFields = fields;
        }
    }
    /**
     * Secondary constructor for initializing a SoliticsEvent instance with custom fields in the form of Record<string, string>.
     * The custom fields are converted to a JSON string before being assigned.
     *
     * @param {string} name - The name or type of the event (transaction name).
     * @param {number} [amount] - The amount associated with the event (optional).
     * @param {Record<string, string>} customFields - Custom fields associated with the event as a Record<string, string>.
     */
    static fromRecord(name, amount, customFields) {
        const fields = JSON.stringify(customFields); // Convert Record<string, string> to a JSON string
        return new SoliticsEvent({ name, amount, fields });
    }
    /**
     * Gets the name (type) of the event.
     *
     * @returns {string} The name or type of the event.
     */
    get name() {
        return this._name;
    }
    /**
     * Sets the name (type) of the event.
     *
     * @param {string} value - The new name or type for the event.
     */
    set name(value) {
        this._name = value;
    }
    /**
     * Gets the amount of the event.
     *
     * @returns {number | undefined} The amount associated with the event, or undefined if not provided.
     */
    get amount() {
        return this._amount;
    }
    /**
     * Sets the amount of the event.
     *
     * @param {number | undefined} value - The new amount associated with the event.
     */
    set amount(value) {
        this._amount = value;
    }
    /**
     * Gets the custom fields associated with the event.
     *
     * @returns {string} A JSON string representing the custom fields for the event.
     */
    get customFields() {
        return this._customFields;
    }
    /**
     * Sets the custom fields for the event.
     *
     * @param {string} value - A JSON string representing the custom fields to be associated with the event.
     * @throws {Error} Throws an error if the provided value is not valid JSON.
     */
    set customFields(value) {
        try {
            JSON.parse(value); // Ensure valid JSON
            this._customFields = value;
        }
        catch (_a) {
            throw new Error("Invalid JSON for customFields");
        }
    }
    /**
     * Converts the current SoliticsEvent instance into a plain JavaScript object.
     *
     * @returns {Object} A plain JavaScript object representing the current instance of SoliticsEvent.
     */
    toObject() {
        return {
            name: this._name,
            amount: this._amount,
            customFields: this._customFields,
        };
    }
    /**
     * Serializes the current SoliticsEvent instance into a JSON string.
     *
     * @returns {string} A JSON string representation of the current instance of SoliticsEvent.
     */
    toJSON() {
        return JSON.stringify(this.toObject());
    }
    /**
     * Creates a new instance of SoliticsEvent from a JSON string.
     * This method parses the JSON and uses it to initialize a new SoliticsEvent object.
     *
     * @param {string} json - A JSON string representing a SoliticsEvent object.
     * @returns {SoliticsEvent} A new instance of SoliticsEvent initialized with the parsed data.
     * @throws {Error} Throws an error if the provided JSON string is invalid.
     */
    static fromJSON(json) {
        var _a;
        try {
            const data = JSON.parse(json);
            return new SoliticsEvent({
                name: data.name,
                amount: data.amount,
                fields: (_a = data.customFields) !== null && _a !== void 0 ? _a : SoliticsEvent.DEFAULT_CUSTOM_FIELDS,
            });
        }
        catch (_b) {
            throw new Error("Invalid JSON string for SoliticsEvent");
        }
    }
    /**
     * Compares the current instance with another SoliticsEvent instance to determine equality.
     *
     * @param {SoliticsEvent} other - The other SoliticsEvent instance to compare with.
     * @returns {boolean} True if all properties of the two instances are equal, otherwise false.
     */
    equals(other) {
        return (this._name === other.name &&
            this._amount === other.amount &&
            this._customFields === other.customFields);
    }
}
/**
 * Static constant for the default value of custom fields.
 */
SoliticsEvent.DEFAULT_CUSTOM_FIELDS = '{}';
